import { useAuth } from "@/hooks/useAuth";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    useGetApartmentOwnersQuery,
    useGetExpenseCategoriesQuery,
    useGetGroupsQuery,
    useGetMunicipalitySectionsQuery,
    useGetOwnerGroupsQuery,
    useGetYearsInOperationQuery,
} from "@/store/booking-management-api";
import { useGetApartmentsQuery } from "@/store/slices/apartments";

export const AppDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!user) {
            navigate("/login");
        }
    }, [navigate, user]);

    // Skip queries if user is not yet loaded
    const skipQueries = !user;
    const isAdmin = user ? user.isAdmin : false;

    const apartments = useGetApartmentsQuery(isAdmin, {
        skip: skipQueries,
    });
    const owners = useGetApartmentOwnersQuery(isAdmin, {
        skip: skipQueries,
    });

    const categories = useGetExpenseCategoriesQuery(undefined, {
        skip: skipQueries,
    });
    const years = useGetYearsInOperationQuery(isAdmin, {
        skip: skipQueries,
    });

    const groups = useGetGroupsQuery(undefined, {
        skip: skipQueries || !isAdmin,
    });

    const municipalitySections = useGetMunicipalitySectionsQuery(undefined, {
        skip: skipQueries,
    });

    const ownerGroups = useGetOwnerGroupsQuery(undefined, {
        skip: skipQueries,
    });

    const isError =
        apartments.isError ||
        owners.isError ||
        groups.isError ||
        categories.isError ||
        years.isError ||
        municipalitySections.isError ||
        ownerGroups.isError;

    const isPending =
        apartments.isLoading ||
        apartments.isUninitialized ||
        owners.isLoading ||
        owners.isUninitialized ||
        categories.isLoading ||
        categories.isUninitialized ||
        years.isLoading ||
        years.isUninitialized ||
        (isAdmin && (groups.isLoading || groups.isUninitialized)) ||
        municipalitySections.isLoading ||
        municipalitySections.isUninitialized ||
        ownerGroups.isLoading ||
        ownerGroups.isUninitialized;

    if (isError) {
        console.log("Error:", {
            apartments,
            owners,
            groups,
            categories,
            years,
        });
        logout();
    }

    if (user && isPending) {
        return <div>Loading...</div>;
    }

    return <>{children}</>;
};
